<script setup lang="ts">
import { computed, ref } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";
import { useRoute, useRouter } from "vue-router";

import {
  createCheckSheet,
  listCheckSheets,
  listFormTemplates,
  listLocations,
} from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import { useProjectStore } from "@/stores/project";

const router = useRouter();
const route = useRoute();
const queryClient = useQueryClient();
const { selectedProjectId } = storeToRefs(useProjectStore());

const { data, isPending, isError, error } = useQuery({
  queryKey: ["check-sheets", selectedProjectId],
  queryFn: () => listCheckSheets(selectedProjectId.value),
});

// Optional ?structure=<id> filter (e.g. arriving from the Progress page).
const structureFilter = computed(() => (route.query.structure as string) || "");
const filteredSheets = computed(() =>
  structureFilter.value
    ? (data.value ?? []).filter((c) => c.structure_id === structureFilter.value)
    : (data.value ?? []),
);
const structureLabel = computed(() => {
  const l = (locations.value ?? []).find((x) => x.id === structureFilter.value);
  return l ? `${l.ref}${l.name ? ` — ${l.name}` : ""}` : structureFilter.value;
});
function clearStructure(): void {
  const q = { ...route.query };
  delete q.structure;
  router.replace({ query: q });
}

const { data: templates } = useQuery({
  queryKey: ["form-templates"],
  queryFn: () => listFormTemplates(),
});

const { data: locations } = useQuery({
  queryKey: ["locations", selectedProjectId],
  queryFn: () => listLocations(selectedProjectId.value),
});

// ---- Office fallback: create a check sheet from the web ----
const showCreate = ref(false);
const form = ref({ templateId: "", structureId: "", inspectionType: "routine" });
const createBusy = ref(false);
const createError = ref<string | null>(null);

const INSPECTION_TYPES = [
  { value: "routine", label: "Routine" },
  { value: "post_install", label: "Post-install" },
  { value: "handback", label: "Handback" },
];

const canCreate = computed(
  () => !!form.value.templateId && !!form.value.structureId,
);

async function create(): Promise<void> {
  const tmpl = (templates.value ?? []).find((t) => t.id === form.value.templateId);
  if (!tmpl || !form.value.structureId) return;
  createBusy.value = true;
  createError.value = null;
  try {
    const id = await createCheckSheet({
      templateId: tmpl.id,
      templateVersion: tmpl.version,
      projectId: selectedProjectId.value as string,
      structureId: form.value.structureId,
      inspectionType: form.value.inspectionType,
    });
    await queryClient.invalidateQueries({ queryKey: ["check-sheets"] });
    void router.push({
      name: "check-sheet-detail",
      params: { projectId: selectedProjectId.value, id },
    });
  } catch (e) {
    createError.value = e instanceof Error ? e.message : String(e);
  } finally {
    createBusy.value = false;
  }
}

const templateName = computed(() => {
  const map = new Map<string, string>();
  for (const t of templates.value ?? []) map.set(t.id, t.name);
  return (id: string): string => map.get(id) ?? id;
});

const STATUS: Record<string, { label: string; cls: string }> = {
  in_progress: { label: "In progress", cls: "bg-neutral-100 text-neutral-500" },
  signed_off: {
    label: "Signed off — awaiting CRE",
    cls: "bg-semantic-warning/10 text-semantic-warning",
  },
  approved: { label: "Approved", cls: "bg-semantic-success/10 text-semantic-success" },
  reopened: { label: "Reopened", cls: "bg-semantic-danger/10 text-semantic-danger" },
};
const statusOf = (s: string) =>
  STATUS[s] ?? { label: s, cls: "bg-neutral-100 text-neutral-500" };

const open = (id: string): void => {
  void router.push({
    name: "check-sheet-detail",
    params: { projectId: selectedProjectId.value, id },
  });
};
</script>

<template>
  <PageLayout>
    <div class="mb-1 flex items-start justify-between gap-4">
      <h1 class="text-xl font-semibold">
        Check sheets
      </h1>
      <button
        type="button"
        class="shrink-0 rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
        @click="showCreate = !showCreate"
      >
        {{ showCreate ? "Cancel" : "+ New check sheet" }}
      </button>
    </div>
    <p class="text-sm text-neutral-500 mb-4">
      QA / ITP check sheets completed on site. Signed-off sheets await OLE CRE
      approval.
    </p>

    <!-- Office fallback create -->
    <div
      v-if="showCreate"
      class="mb-4 rounded-xl border border-neutral-200 bg-neutral-0 p-4"
    >
      <p class="mb-3 text-sm text-neutral-500">
        Office fallback — normally a check sheet is started on site. This creates
        an empty sheet to be completed on the mobile app.
      </p>
      <div
        v-if="(locations ?? []).length === 0"
        class="text-sm text-semantic-warning"
      >
        This project has no structures yet — import or add one under
        <RouterLink
          :to="{ name: 'structures', params: { projectId: selectedProjectId } }"
          class="underline"
        >
          Structures
        </RouterLink>
        first.
      </div>
      <div
        v-else
        class="grid gap-3 sm:grid-cols-3"
      >
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">ITP template</span>
          <select
            v-model="form.templateId"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
            <option
              value=""
              disabled
            >
              Select…
            </option>
            <option
              v-for="t in templates ?? []"
              :key="t.id"
              :value="t.id"
            >
              {{ t.name }}
            </option>
          </select>
        </label>
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">Structure</span>
          <select
            v-model="form.structureId"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
            <option
              value=""
              disabled
            >
              Select…
            </option>
            <option
              v-for="l in locations ?? []"
              :key="l.id"
              :value="l.id"
            >
              {{ l.ref }}{{ l.name ? " — " + l.name : "" }}
            </option>
          </select>
        </label>
        <label class="grid gap-1 text-sm">
          <span class="text-neutral-500">Type</span>
          <select
            v-model="form.inspectionType"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          >
            <option
              v-for="t in INSPECTION_TYPES"
              :key="t.value"
              :value="t.value"
            >
              {{ t.label }}
            </option>
          </select>
        </label>
      </div>
      <div
        v-if="(locations ?? []).length"
        class="mt-3 flex items-center gap-3"
      >
        <button
          type="button"
          class="rounded-md bg-brand-primary px-4 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
          :disabled="!canCreate || createBusy"
          @click="create"
        >
          {{ createBusy ? "Creating…" : "Create check sheet" }}
        </button>
        <span
          v-if="createError"
          class="text-sm text-semantic-danger"
        >{{ createError }}</span>
      </div>
    </div>

    <div
      v-if="structureFilter"
      class="mb-3 flex items-center gap-2 rounded-md bg-brand-primary/5 px-3 py-2 text-sm"
    >
      <span>Showing check sheets for <strong>{{ structureLabel }}</strong></span>
      <button
        type="button"
        class="text-brand-primary hover:underline"
        @click="clearStructure"
      >
        Clear
      </button>
    </div>
    <div
      v-if="isPending"
      class="text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="bg-semantic-danger/10 text-semantic-danger rounded-md p-4"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data && data.length === 0"
      class="text-neutral-500"
    >
      No check sheets have been completed on this project yet.
    </div>
    <div
      v-else-if="data"
      class="rounded-xl bg-neutral-0 shadow overflow-hidden"
    >
      <table class="w-full text-sm">
        <thead class="bg-neutral-50 text-neutral-500 text-left">
          <tr>
            <th class="px-4 py-2 font-medium">
              Check sheet
            </th>
            <th class="px-4 py-2 font-medium">
              Type
            </th>
            <th class="px-4 py-2 font-medium">
              Updated
            </th>
            <th class="px-4 py-2 font-medium">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="c in filteredSheets"
            :key="c.id"
            class="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer"
            @click="open(c.id)"
          >
            <td class="px-4 py-2">
              {{ templateName(c.template_id) }}
            </td>
            <td class="px-4 py-2 text-neutral-500">
              {{ c.inspection_type.replace("_", " ") }}
            </td>
            <td class="px-4 py-2 text-neutral-500">
              {{ new Date(c.last_updated_at).toLocaleDateString() }}
            </td>
            <td class="px-4 py-2">
              <span
                class="rounded-full px-2 py-0.5 text-xs"
                :class="statusOf(c.status).cls"
              >
                {{ statusOf(c.status).label }}
              </span>
            </td>
          </tr>
          <tr v-if="filteredSheets.length === 0">
            <td
              colspan="4"
              class="px-4 py-6 text-center text-neutral-400"
            >
              No check sheets for this location yet.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </PageLayout>
</template>
